import React, { useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { Download, Loader2, Music2, Share2, Trash2, X } from "lucide-react";
import { MoodJourneyChoices } from "../journey/MoodJourneyChoices";
import { API, auth, TOK, SERIF, SANS, PrimaryBtn } from "./shared";

const pill = { border: `1px solid ${TOK.goldMist}`, color: TOK.creamDim, fontFamily: SANS, fontSize: "0.6rem", letterSpacing: "0.22em" };
const Pill = ({ children, testId, ...p }) => (
  <button type="button" data-testid={testId} {...p}
    className="rounded-full px-4 py-2 uppercase inline-flex items-center gap-1.5 transition-colors duration-200 hover:bg-white/5 disabled:opacity-50" style={pill}>
    {children}
  </button>
);
const fileName = (j) => `${(j.name || "soulumo").replace(/[^A-Za-z0-9]+/g, "_").replace(/^_|_$/g, "").toLowerCase() || "soulumo"}.mp3`;

// Phones: first tap fetches the file, second tap opens the share sheet (Save to Files, WhatsApp, AirDrop…).
export const SaveAudioButton = ({ j }) => {
  const [state, setState] = useState("idle");
  const [file, setFile] = useState(null);
  const fetchFile = async () => {
    setState("loading");
    try {
      const res = await fetch(`${API}/api/audio-journeys/${j.id}/audio?download=1`);
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const f = new File([blob], fileName(j), { type: "audio/mpeg" });
      if (navigator.canShare?.({ files: [f] })) { setFile(f); setState("ready"); return; }
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: f.name });
      document.body.appendChild(a); a.click(); a.remove();
      setState("saved");
    } catch { setState("error"); }
  };
  const share = async () => {
    try { await navigator.share({ files: [file], title: j.name }); setState("saved"); } catch { /* sheet closed */ }
  };
  if (state === "ready") return <Pill testId={`install-save-share-${j.id}`} onClick={share}><Share2 size={12} /> Save or send the file</Pill>;
  return (
    <Pill testId={`install-save-${j.id}`} onClick={fetchFile} disabled={state === "loading"}>
      {state === "loading" ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
      {state === "loading" ? "Preparing…" : state === "saved" ? "Saved · again?" : state === "error" ? "Try again" : "Save to phone"}
    </Pill>
  );
};

export const DeleteInstallButton = ({ j, onDeleted }) => {
  const [busy, setBusy] = useState(false);
  const del = async () => {
    setBusy(true);
    try { await axios.post(`${API}/api/audio-journeys/bulk-trash`, { ids: [j.id] }, auth()); onDeleted?.(); }
    catch { window.alert("That didn't go through. Please try again."); } finally { setBusy(false); }
  };
  return <Pill testId={`install-delete-${j.id}`} onClick={del} disabled={busy}><Trash2 size={12} /> {busy ? "Deleting…" : "Delete"}</Pill>;
};

const ERR = {
  402: "You've used the 3 free music changes for this installation. Create a new one for more.",
  409: "Keep this installation first, then you can try different music.",
};

// Same words and voice, different music journey. Free up to 3 times.
export const MusicSheet = ({ j, onDone, onClose }) => {
  const [ids, setIds] = useState([]);
  const left = 3 - (j.music_changes || 0);
  const [keep, setKeep] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const go = async () => {
    setBusy(true); setError("");
    try {
      const r = await axios.post(`${API}/api/audio-journeys/${j.id}/remusic`, { music_ids: ids, keep_original: keep }, auth());
      onDone(r.data);
    } catch (e) { setError(ERR[e?.response?.status] || "Couldn't start that just now. Please try again."); setBusy(false); }
  };
  return createPortal(
    <div className="fixed inset-0 z-[130] flex items-end sm:items-center justify-center p-3" style={{ background: "rgba(3,7,16,0.8)", backdropFilter: "blur(10px)" }} data-testid="music-sheet">
      <div className="w-full max-w-lg rounded-3xl p-6 max-h-[90vh] overflow-y-auto" style={{ background: "#0d1f3a", border: `1px solid ${TOK.goldMist}` }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[0.6rem] uppercase tracking-[0.3em]" style={{ color: TOK.goldSoft, fontFamily: SANS }}>Same words · new music</div>
            <h3 className="mt-1 text-2xl" style={{ fontFamily: SERIF, color: TOK.cream }}>Choose a music journey</h3>
            <p className="mt-1 text-xs" style={{ color: TOK.creamMist, fontFamily: SANS }}>No credit used · {left > 0 ? `${left} free change${left === 1 ? "" : "s"} left` : "free changes used"}. Tap ▶ to hear a sample.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="music-sheet-close" className="w-9 h-9 shrink-0 rounded-full inline-flex items-center justify-center" style={{ border: `1px solid ${TOK.goldMist}`, color: TOK.cream }}><X size={15} /></button>
        </div>
        <div className="mt-5">
          <MoodJourneyChoices theme={j.affirmation_theme} concern={j.custom_concern || ""} selectedIds={ids} currentIds={j.music_ids} autoSelect={false} onSelect={(next) => setIds(next)} />
        </div>
        <div className="grid grid-cols-2 gap-2" data-testid="music-sheet-keep-choice">
          {[[true, "Keep both versions"], [false, "Replace this one"]].map(([v, label]) => (
            <button key={label} type="button" onClick={() => setKeep(v)} data-testid={`music-sheet-${v ? "keep" : "replace"}`}
              className="rounded-xl py-2.5 text-xs transition-colors duration-200" style={{ fontFamily: SANS, border: `1px solid ${keep === v ? TOK.gold : TOK.goldMist}`, color: keep === v ? TOK.cream : TOK.creamDim, background: keep === v ? "rgba(215,187,122,0.12)" : "transparent" }}>
              {label}
            </button>
          ))}
        </div>
        {error && <p className="mt-3 text-sm" style={{ color: "#e9a39c", fontFamily: SANS }} data-testid="music-sheet-error">{error}</p>}
        <div className="mt-5">
          <PrimaryBtn testId="music-sheet-go" disabled={!ids.length || busy} onClick={go}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Music2 size={16} />} Create with this music
          </PrimaryBtn>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export const MusicChangeButton = ({ j, onDone }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Pill testId={`install-remusic-${j.id}`} onClick={() => setOpen(true)}><Music2 size={12} /> Different music</Pill>
      {open && <MusicSheet j={j} onClose={() => setOpen(false)} onDone={(nj) => { setOpen(false); onDone?.(nj); }} />}
    </>
  );
};

export const InstallationActions = ({ j, onChanged }) => (
  <div className="mt-3 flex gap-2 flex-wrap pr-14 sm:pr-0" data-testid={`install-actions-${j.id}`}>
    {!j.clone_trial_pending && <SaveAudioButton j={j} />}
    {!j.clone_trial_pending && <MusicChangeButton j={j} onDone={onChanged} />}
    <DeleteInstallButton j={j} onDeleted={onChanged} />
  </div>
);
